import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import Image from '@fec/frontend/elements/media/image/Image.jsx';
import TeaserInfo from '@fec/frontend/elements/text/teaser-info/teaser-info.js';
import { getConfig } from '@fec/assets/js/utils/config.js';
import PlayIcon from '@fec/frontend/elements/icons/play-icon/PlayIcon.jsx';
import MediaProgressBar from '@fec/frontend/elements/media/media-progress-bar/MediaProgressBar.jsx';

import {
  onEvent,
  TEASER_VIDEO_CLICK,
  triggerEvent,
  VIDEO_MODAL_CLOSED,
} from '@fec/assets/js/utils/event.js';
import usePlayerAppEvents from '@fec/frontend-aron/foundation/hooks/usePlayerAppEvents.jsx';
import {
  formatDate,
  formatTime,
} from '@fec/assets/js/utils/date-time-formatter';
import { shouldShowMediaProgress } from '@fec/frontend/elements/media/media-progress-bar/media-progress-bar';
import { getMediumProgressPercentage } from '@fec/assets/js/utils/media-progress';
import PseudoLink from '@fec/frontend/elements/links/pseudo-link/PseudoLink.jsx';

import './related-item.scss';

const { REPRESENTATION = 'web' } = getConfig();

const getTrackData = (type, currentArticleId) => {
  let trackData = {
    srg_mod1: 'content',
    srg_mod2: `${type}_teaser`,
    srg_mod3: 'more',
  };

  if (currentArticleId) {
    trackData['srg_mod4'] = currentArticleId;
  }

  return new URLSearchParams(trackData).toString();
};

const getLinkToUse = (item) => {
  if (item?.representationLinks && item.representationLinks[REPRESENTATION]) {
    return item.representationLinks[REPRESENTATION];
  } else if (item?.link) {
    return item.link;
  } else {
    return '#';
  }
};

const RelatedItem = ({ item, currentArticleId, modifier }) => {
  let RelatedItemComponent;

  if (
    ['audio', 'video'].includes(item.type) &&
    REPRESENTATION.includes('webview')
  ) {
    // Audio and Video are so similar in the app that it warrants a unified component
    RelatedItemComponent = RelatedMediaApp;
  } else if (item.type === 'video') {
    RelatedItemComponent = RelatedVideo;
  } else if (item.type === 'audio') {
    RelatedItemComponent = RelatedAudio;
  } else if (item.type === 'article') {
    RelatedItemComponent = RelatedArticle;
  } else {
    return null;
  }

  const trackData = getTrackData(item.type, currentArticleId);
  return <RelatedItemComponent {...{ item, trackData, modifier }} />;
};

const RelatedMediaApp = ({ item, trackData, modifier }) => {
  const { t } = useTranslation();
  const [isActive, setIsActive] = useState(false);

  const {
    dispatchMediaClickedEvents,
    dispatchMediaStoppedEvents,
    hasActivePlayer,
  } = usePlayerAppEvents(item.urn, item.bridgePayload);

  useEffect(() => {
    setIsActive(hasActivePlayer);
  }, [hasActivePlayer]);

  const handleInteraction = () => {
    if (isActive) {
      dispatchMediaStoppedEvents();
    } else {
      dispatchMediaClickedEvents();
    }
  };

  const handleInteractionByKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleInteraction();
    }
  };

  const playMediaTranslation = {
    video: t('Video abspielen: '),
    audio: t('Audio abspielen: '),
  };

  return (
    <div
      className={classNames('related-item', modifier)}
      data-referrer-track={trackData}
      onClick={() => handleInteraction()}
      onKeyDown={(e) => handleInteractionByKey(e)}>
      <RelatedItemImage item={item} isActive={isActive} />

      <div className="related-item__content">
        <span className="h-offscreen">{playMediaTranslation[item.type]}</span>
        <h3 className="related-item__title">{item.title}</h3>
        <RelatedItemSource item={item} />
      </div>
    </div>
  );
};

const RelatedVideo = ({ item, trackData, modifier }) => {
  const { t } = useTranslation();
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    onEvent({
      eventName: TEASER_VIDEO_CLICK,
      eventHandler: (event) => {
        setIsActive(event.detail.assetId === item.urn);
      },
    });

    onEvent({
      eventName: VIDEO_MODAL_CLOSED,
      eventHandler: () => {
        setIsActive(false);
      },
    });
  }, [item]);

  const triggerModal = () => {
    triggerEvent(TEASER_VIDEO_CLICK, {
      id: item.frontendId,
      assetId: item.urn,
    });
  };
  const onClick = () => triggerModal();
  const onKeyPress = (e) => {
    if (e.key === 'Enter') {
      triggerModal();
      e.preventDefault();
    }
  };

  // special sausage for audios, so that they aren't read in pieces but as one single entity
  const ariaLabel = t(
    'Video «{{title}}» abspielen. Aus {{show}} vom {{date}}, {{time}} Uhr.',
    {
      title: item.title,
      show: item.show,
      date: formatDate(item.date),
      time: formatTime(item.date),
    },
  );

  return (
    <div
      className={classNames('related-item', modifier)}
      onClick={onClick}
      onKeyDown={onKeyPress}
      data-referrer-track={trackData}
      aria-label={ariaLabel}
      tabIndex={0}>
      <RelatedItemImage item={item} isActive={isActive} />

      <div className="related-item__content" aria-hidden="true">
        <h3 className="related-item__title">{item.title}</h3>
        <RelatedItemSource item={item} />
        {item.description?.length > 0 && (
          <div className="related-item__description">{item.description}</div>
        )}
      </div>
    </div>
  );
};

const RelatedAudio = ({ item, trackData, modifier }) => {
  const { t } = useTranslation();
  let progressHint = '';
  if (shouldShowMediaProgress()) {
    const progressPercentage = getMediumProgressPercentage(item.urn);
    if (progressPercentage > 0) {
      progressHint = t('playerWidget.progressHint', {
        percentage: Math.ceil(progressPercentage),
      });
    }
  }

  return (
    <a
      className={classNames('related-item', modifier)}
      href={getLinkToUse(item)}
      data-referrer-track={trackData}>
      <RelatedItemImage item={item} />
      <div className="related-item__content">
        <span className="h-offscreen">
          {t('Hinweis auf ein verwandtes Audio: ')}
        </span>
        <h3 className="related-item__title">{item.title}</h3>

        <RelatedItemSource item={item} />
        {progressHint && <span className="h-offscreen">{progressHint} </span>}
      </div>
    </a>
  );
};

const RelatedArticle = ({ item, trackData, modifier }) => {
  const { t } = useTranslation();

  const modDate = new Date(item.modificationDate);
  const pubDate = new Date(item.publicationDate);
  const dateToShow = formatDate(modDate > pubDate ? modDate : pubDate);

  const mediaIconTranslations = {
    video: t('Mit Video'),
    audio: t('Mit Audio'),
    image: t('Mit Bildergalerie'),
  };

  return (
    <a
      className={classNames('related-item', modifier)}
      href={getLinkToUse(item)}
      data-referrer-track={trackData}
      data-urn={item.urn}
      data-title={item.title}>
      <RelatedItemImage item={item} />
      <div className="related-item__content">
        <span className="h-offscreen">
          {t('Hinweis auf einen verwandten Artikel:')}
        </span>
        {item.kicker && <p className="related-item__kicker">{item.kicker}</p>}
        <h3 className="related-item__title">{item.title}</h3>
      </div>

      {(dateToShow || item.mediaIcon) && (
        <div className="related-item__meta-container">
          {dateToShow && (
            <TeaserInfo styleModifier="teaser-info--fading" text={dateToShow} />
          )}

          {item.mediaIcon && (
            <TeaserInfo
              styleModifier={`teaser-info--${item.mediaIcon}`}
              text={mediaIconTranslations[item.mediaIcon] ?? ''}
            />
          )}
        </div>
      )}
    </a>
  );
};

const RelatedItemImage = ({ item, isActive = false }) => {
  const showPlayIcon = item.type === 'video' || item.type === 'audio';
  const showProgressBar = item.type === 'audio' && shouldShowMediaProgress();

  return (
    <div className="related-item__image" aria-hidden="true">
      <Image image={item.image} />
      {showPlayIcon && (
        <div className="related-item__play-icon-container">
          <PlayIcon type={item.type} isActive={isActive} />
        </div>
      )}
      {showProgressBar && <MediaProgressBar urn={item.urn} />}
    </div>
  );
};

const RelatedItemSource = ({ item }) => {
  const { t } = useTranslation();

  if (!item.show && !item.date) {
    return null;
  }

  return (
    <p className="related-item__source">
      {item.showLink && item.show && (
        <PseudoLink link={item.showLink} title={item.show} />
      )}
      {!item.showLink && item.show && <>{item.show}</>}
      {item.show && item.date && <> </>}
      {item.date &&
        t('relatedItem.source', {
          date: formatDate(item.date),
          time: formatTime(item.date),
        })}
      <span className="h-offscreen">.</span>
    </p>
  );
};

export default RelatedItem;
